import { StrictMode, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { drobek } from 'drobek';
import './styles.css';

type Who = 'kamila' | 'petr';

const PEOPLE: Record<Who, { full: string; nick: string; gen: string; dat: string; loc: string; voc: string; f: boolean }> = {
  kamila: { full: 'Kamila', nick: 'Kamča', gen: 'Kamči', dat: 'Kamče', loc: 'Kamče', voc: 'Kamčo', f: true },
  petr: { full: 'Petr', nick: 'Péťa', gen: 'Péti', dat: 'Péťovi', loc: 'Péťovi', voc: 'Péťo', f: false },
};

const SCALE = [
  { label: 'Nikdy', value: 1 },
  { label: 'Zřídka', value: 2 },
  { label: 'Občas', value: 3 },
  { label: 'Často', value: 4 },
  { label: 'Vždy', value: 5 },
  { label: 'Nevím', value: 0 },
];

const SECTIONS: { title: string; items: string[] }[] = [
  {
    title: 'Spolupráce a férovost',
    items: [
      'Dodržuje, co slíbí.',
      'Přizná vlastní chybu a nese za ni odpovědnost.',
      'Respektuje jiný názor a umí přehodnotit svůj postoj.',
      'Aktivně spolupracuje s lidmi z jiných týmů.',
      'Dá zpětnou vazbu, i když je nepříjemná.',
    ],
  },
  {
    title: 'Komunikace',
    items: [
      'Vyjadřuje se jasně a srozumitelně.',
      'Aktivně naslouchá připomínkám a otázkám.',
      'Sdílí informace včas a otevřeně.',
    ],
  },
  {
    title: 'Operativa',
    items: [
      'Řeší praktické problémy efektivně a bez zbytečných průtahů.',
      'Předvídá rizika dřív, než nastanou.',
      'Najde pragmatické řešení i s omezenými zdroji.',
    ],
  },
  {
    title: 'Nápady',
    items: [
      'Přichází s novými nápady a přístupy.',
      'Zpochybní zavedený postup, když to dává smysl.',
    ],
  },
  {
    title: 'Organizace',
    items: [
      'Drží termíny a závazky.',
      'Zvládne víc projektů naráz bez ztráty kvality.',
      'Má jasné priority.',
    ],
  },
  {
    title: 'Pod tlakem',
    items: [
      'Zvládá tlak a nejistotu klidně a věcně.',
      'Je otevřený novým způsobům práce a nástrojům.',
      'Dotahuje úkoly a projekty do konce.',
    ],
  },
];

const ITEM_KEYS = SECTIONS.flatMap((s) => s.items.map((i) => `${s.title}: ${i}`));

// Krátké názvy polí pro e-mail (drobek forms bere názvy polí max 64 znaků)
const SHORT: Record<string, string> = {
  'Dodržuje, co slíbí.': 'Spolupráce · Dodržuje sliby',
  'Přizná vlastní chybu a nese za ni odpovědnost.': 'Spolupráce · Přizná chybu a nese odpovědnost',
  'Respektuje jiný názor a umí přehodnotit svůj postoj.': 'Spolupráce · Umí přehodnotit názor',
  'Aktivně spolupracuje s lidmi z jiných týmů.': 'Spolupráce · Spolupracuje s jinými týmy',
  'Dá zpětnou vazbu, i když je nepříjemná.': 'Spolupráce · Dá i nepříjemnou zpětnou vazbu',
  'Vyjadřuje se jasně a srozumitelně.': 'Komunikace · Mluví jasně',
  'Aktivně naslouchá připomínkám a otázkám.': 'Komunikace · Naslouchá',
  'Sdílí informace včas a otevřeně.': 'Komunikace · Sdílí informace včas',
  'Řeší praktické problémy efektivně a bez zbytečných průtahů.': 'Operativa · Řeší problémy bez průtahů',
  'Předvídá rizika dřív, než nastanou.': 'Operativa · Předvídá rizika',
  'Najde pragmatické řešení i s omezenými zdroji.': 'Operativa · Pragmatická řešení',
  'Přichází s novými nápady a přístupy.': 'Nápady · Přichází s novými nápady',
  'Zpochybní zavedený postup, když to dává smysl.': 'Nápady · Zpochybní zavedený postup',
  'Drží termíny a závazky.': 'Organizace · Drží termíny',
  'Zvládne víc projektů naráz bez ztráty kvality.': 'Organizace · Víc projektů bez ztráty kvality',
  'Má jasné priority.': 'Organizace · Jasné priority',
  'Zvládá tlak a nejistotu klidně a věcně.': 'Pod tlakem · Klid a věcnost',
  'Je otevřený novým způsobům práce a nástrojům.': 'Pod tlakem · Otevřenost novým nástrojům',
  'Dotahuje úkoly a projekty do konce.': 'Pod tlakem · Dotahuje do konce',
};
const shortKey = (itemKey: string) => {
  const text = itemKey.slice(itemKey.indexOf(': ') + 2);
  return (SHORT[text] || itemKey).slice(0, 64);
};

function openQuestions(filler: Who) {
  const t = PEOPLE[filler === 'kamila' ? 'petr' : 'kamila'];
  const me = PEOPLE[filler];
  return [
    { key: 'Co oceňuješ', q: `Co na ${t.loc} oceňuješ?` },
    { key: 'Podnět ke zlepšení', q: `Co bys ${me.f ? 'přidala' : 'přidal'} jako podnět ke zlepšení u ${t.gen}?` },
    { key: 'Na co se zaměřit', q: `Na co by se ${t.f ? 'měla' : 'měl'} ${t.nick} zaměřit v příštích 6–12 měsících?` },
    { key: 'Víc a míň', q: `Co od ${t.gen} potřebuješ víc a co míň?` },
  ];
}

function bonusQuestions(filler: Who) {
  const t = PEOPLE[filler === 'kamila' ? 'petr' : 'kamila'];
  const list = [
    {
      key: 'Kafe před standupem',
      q: 'Kdo dřív dorazí na kafe před standupem?',
      options: ['Já', t.nick, 'Chodíme spolu', 'Kafe až po standupu'],
    },
    {
      key: 'Stav ticketu',
      q: 'Kdyby naše spolupráce byla Jira ticket, v jakém by byla stavu?',
      options: ['Úkoly', 'V běhu', 'Hotovo', 'Blokováno, čeká na fakturu'],
    },
    {
      key: 'Story pointy porady',
      q: 'Kolik story pointů má průměrná porada?',
      options: ['1', '3', '8', '∞'],
    },
  ];
  list.push(
    filler === 'petr'
      ? {
          key: 'Zastupování',
          q: 'Jak se ti zastupovalo podle předávky?',
          options: ['V pohodě', 'Přežil jsem', 'Zachránil mě komiks', 'Ještě se z toho dostávám'],
        }
      : {
          key: 'Návrat ze zástupu',
          q: 'V jakém stavu jsi po návratu našla agendu?',
          options: ['Všechno běželo', 'Pár věcí na mě počkalo', 'Nic nespadlo pod stůl', 'Péťa to zvládl líp než já'],
        }
  );
  return list;
}

const TRUST = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

type Draft = { scale: Record<string, number>; text: Record<string, string>; bonus: Record<string, string>; word: string; trust?: number };
const emptyDraft = (): Draft => ({ scale: {}, text: {}, bonus: {}, word: '' });

function loadDraft(who: Who): Draft {
  try {
    const raw = localStorage.getItem('kafe-draft-' + who);
    if (raw) return { ...emptyDraft(), ...JSON.parse(raw) };
  } catch {}
  return emptyDraft();
}
function saveDraft(who: Who, d: Draft) {
  try {
    localStorage.setItem('kafe-draft-' + who, JSON.stringify(d));
  } catch {}
}
function clearDraft(who: Who) {
  try {
    localStorage.removeItem('kafe-draft-' + who);
  } catch {}
}

const today = new Date().toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' });

function App() {
  const [who, setWho] = useState<Who | null>(null);
  const [draft, setDraft] = useState<Draft>(emptyDraft);
  const [missing, setMissing] = useState<string[]>([]);
  const [state, setState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [notified, setNotified] = useState(true);
  const [error, setError] = useState('');
  const [errDetail, setErrDetail] = useState('');
  const firstMissingRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    void drobek.forms.prepare('zpetna-vazba');
  }, []);

  useEffect(() => {
    if (who) saveDraft(who, draft);
  }, [who, draft]);

  const pick = (w: Who) => {
    setWho(w);
    setDraft(loadDraft(w));
    setMissing([]);
  };

  const answered = ITEM_KEYS.filter((k) => draft.scale[k] !== undefined).length;
  const avg = useMemo(() => {
    const vals = Object.values(draft.scale).filter((v) => v > 0);
    if (!vals.length) return null;
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  }, [draft.scale]);

  const setScale = (k: string, v: number) => {
    setDraft((d) => ({ ...d, scale: { ...d.scale, [k]: v } }));
    setMissing((m) => m.filter((x) => x !== k));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!who) return;
    const miss = ITEM_KEYS.filter((k) => draft.scale[k] === undefined);
    if (draft.trust === undefined) miss.push('trust');
    if (miss.length) {
      setMissing(miss);
      requestAnimationFrame(() => firstMissingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
      return;
    }
    const target = who === 'kamila' ? 'petr' : 'kamila';
    const data: Record<string, string> = {
      Vyplňuje: PEOPLE[who].full,
      Hodnotí: PEOPLE[target].full,
      Průměr: avg === null ? '–' : avg.toFixed(1).replace('.', ','),
    };
    for (const k of ITEM_KEYS) {
      const v = draft.scale[k];
      const lbl = SCALE.find((s) => s.value === v)!.label;
      data[shortKey(k)] = v > 0 ? `${lbl} (${v})` : 'Nemohu posoudit';
    }
    data[`Jak moc věříš ${PEOPLE[target].dat}?`] = `${draft.trust} %`;
    for (const o of openQuestions(who)) data[o.q.slice(0, 64)] = (draft.text[o.key] || '').trim() || '–';
    for (const b of bonusQuestions(who)) data['Bonus · ' + b.key] = draft.bonus[b.key] || '–';
    data['Spropitné · slovo pro naše duo'] = draft.word.trim() || '–';

    setState('sending');
    setError('');
    try {
      const res = await drobek.forms.submit('zpetna-vazba', data);
      setNotified(res.notified);
      setState('sent');
      clearDraft(who);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      console.error(err instanceof Error ? err : new Error(String(err)));
      setErrDetail([err?.status, err?.code, err?.message, err?.details ? JSON.stringify(err.details) : ''].filter(Boolean).join(' · '));
      setState('error');
      setError(
        err?.code === 'rate_limited'
          ? 'Odesláno moc účtenek za hodinu. Zkus to za chvíli, odpovědi zůstanou vyplněné.'
          : 'Odeslání se nepovedlo. Odpovědi zůstaly vyplněné, zkus to znovu.'
      );
    }
  };

  if (state === 'sent' && who) {
    const t = PEOPLE[who === 'kamila' ? 'petr' : 'kamila'];
    return (
      <main className="counter">
        <section className="receipt done">
          <p className="eyebrow">Kafe před standupem · účtenka č. 360</p>
          <div className="stamp">Odesláno</div>
          <h1>Díky, {PEOPLE[who].voc}.</h1>
          {notified ? (
            <p>Odpovědi odešly e-mailem. {t.nick} je uvidí taky, takže to rovnou proberte u kafe.</p>
          ) : (
            <p>Odpovědi jsou uložené, jen se teď nepodařilo poslat e-mail. Kamila je najde v drobku.</p>
          )}
          <div className="total">
            <span>Průměr</span>
            <span className="dots" />
            <span>{avg === null ? '–' : avg.toFixed(1).replace('.', ',')} / 5</span>
          </div>
        </section>
      </main>
    );
  }

  let missingAssigned = false;

  return (
    <main className="counter">
      <form className="receipt" onSubmit={submit} noValidate>
        <header className="head">
          <p className="eyebrow">Kafe před standupem · účtenka č. 360</p>
          <h1>Zpětná vazba Kamča ↔ Péťa</h1>
          <p className="lede">
            Otázky jsou z firemního 360° dotazníku. Nechala jsem jen ty, které dávají smysl mezi dvěma kolegy, a na konec
            přidala pár bonusových. Odpovědi přijdou e-mailem nám oběma.
          </p>
          <dl className="meta">
            <div>
              <dt>Datum</dt>
              <dd>{today}</dd>
            </div>
            <div>
              <dt>Položek</dt>
              <dd>{ITEM_KEYS.length + 1}</dd>
            </div>
            <div>
              <dt>Trvá</dt>
              <dd>asi 10 min</dd>
            </div>
          </dl>
        </header>

        <fieldset className="who">
          <legend>Kdo vyplňuje?</legend>
          <div className="who-row">
            {(['kamila', 'petr'] as Who[]).map((w) => (
              <button
                type="button"
                key={w}
                className={'who-btn' + (who === w ? ' on' : '')}
                aria-pressed={who === w}
                onClick={() => pick(w)}
              >
                <span className="who-name">{PEOPLE[w].full}</span>
                <span className="who-sub">hodnotím {w === 'kamila' ? 'Péťu' : 'Kamču'}</span>
              </button>
            ))}
          </div>
        </fieldset>

        {!who && <p className="hint">Vyber, kdo jsi, a otázky se ukážou.</p>}

        {who && (
          <>
            <p className="scale-legend">
              U každé věty vyber, jak často to podle tebe platí. Když to nemáš jak posoudit, dej <b>Nevím</b>.
            </p>

            {SECTIONS.map((s) => {
              const keys = s.items.map((i) => `${s.title}: ${i}`);
              const done = keys.filter((k) => draft.scale[k] !== undefined).length;
              return (
                <section className="section" key={s.title}>
                  <h2 className="line">
                    <span>{s.title}</span>
                    <span className="dots" />
                    <span className="count">
                      {done}/{keys.length}
                    </span>
                  </h2>
                  {s.items.map((item, idx) => {
                    const k = keys[idx];
                    const isMissing = missing.includes(k);
                    let ref: ((el: HTMLDivElement | null) => void) | undefined;
                    if (isMissing && !missingAssigned) {
                      missingAssigned = true;
                      ref = (el) => {
                        firstMissingRef.current = el;
                      };
                    }
                    const id = 'q' + ITEM_KEYS.indexOf(k);
                    return (
                      <div className={'item' + (isMissing ? ' missing' : '')} key={k} ref={ref}>
                        <p className="q" id={id + '-l'}>
                          {item}
                        </p>
                        <div className="chips" role="radiogroup" aria-labelledby={id + '-l'}>
                          {SCALE.map((sc) => (
                            <label key={sc.value} className={'chip' + (draft.scale[k] === sc.value ? ' on' : '') + (sc.value === 0 ? ' na' : '')}>
                              <input
                                type="radio"
                                name={id}
                                id={`${id}-${sc.value}`}
                                checked={draft.scale[k] === sc.value}
                                onChange={() => setScale(k, sc.value)}
                              />
                              {sc.label}
                            </label>
                          ))}
                        </div>
                        {isMissing && <p className="miss-note">Tohle ještě chybí.</p>}
                      </div>
                    );
                  })}
                </section>
              );
            })}

            <section className="section">
              <h2 className="line">
                <span>Důvěra</span>
                <span className="dots" />
                <span className="count">{draft.trust === undefined ? '0/1' : `${draft.trust} %`}</span>
              </h2>
              {(() => {
                const isMissing = missing.includes('trust');
                const ref = isMissing && !missingAssigned ? ((missingAssigned = true), (el: HTMLDivElement | null) => {
                  firstMissingRef.current = el;
                }) : undefined;
                return (
                  <div className={'item' + (isMissing ? ' missing' : '')} ref={ref}>
                    <p className="q" id="trust-l">
                      Jak moc věříš {PEOPLE[who === 'kamila' ? 'petr' : 'kamila'].dat}?
                    </p>
                    <div className="chips trust" role="radiogroup" aria-labelledby="trust-l">
                      {TRUST.map((v) => (
                        <label key={v} className={'chip' + (draft.trust === v ? ' on' : '')}>
                          <input
                            type="radio"
                            name="trust"
                            id={'trust-' + v}
                            checked={draft.trust === v}
                            onChange={() => {
                              setDraft((d) => ({ ...d, trust: v }));
                              setMissing((m) => m.filter((x) => x !== 'trust'));
                            }}
                          />
                          {v}&nbsp;%
                        </label>
                      ))}
                    </div>
                    {isMissing && <p className="miss-note">Tohle ještě chybí.</p>}
                  </div>
                );
              })()}
            </section>

            <section className="section">
              <h2 className="line">
                <span>Vlastními slovy</span>
                <span className="dots" />
                <span className="count">nepovinné</span>
              </h2>
              {openQuestions(who).map((o) => (
                <div className="item" key={o.key}>
                  <label className="q" htmlFor={'t-' + o.key}>
                    {o.q}
                  </label>
                  <textarea
                    id={'t-' + o.key}
                    rows={3}
                    value={draft.text[o.key] || ''}
                    onChange={(e) => setDraft((d) => ({ ...d, text: { ...d.text, [o.key]: e.target.value } }))}
                  />
                </div>
              ))}
            </section>

            <section className="section bonus">
              <h2 className="line">
                <span>Bonus k účtence</span>
                <span className="dots" />
                <span className="count">zdarma</span>
              </h2>
              {bonusQuestions(who).map((b, bi) => (
                <div className="item" key={b.key}>
                  <p className="q" id={'b' + bi + '-l'}>
                    {b.q}
                  </p>
                  <div className="chips wide" role="radiogroup" aria-labelledby={'b' + bi + '-l'}>
                    {b.options.map((opt, oi) => (
                      <label key={opt} className={'chip' + (draft.bonus[b.key] === opt ? ' on' : '')}>
                        <input
                          type="radio"
                          name={'b' + bi}
                          id={`b${bi}-${oi}`}
                          checked={draft.bonus[b.key] === opt}
                          onChange={() => setDraft((d) => ({ ...d, bonus: { ...d.bonus, [b.key]: opt } }))}
                        />
                        {opt}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
              <div className="item">
                <label className="q" htmlFor="word">
                  Spropitné: jedno slovo pro naše duo
                </label>
                <input
                  id="word"
                  className="word"
                  maxLength={40}
                  value={draft.word}
                  onChange={(e) => setDraft((d) => ({ ...d, word: e.target.value }))}
                />
              </div>
            </section>

            <footer className="foot">
              <div className="total">
                <span>Zodpovězeno</span>
                <span className="dots" />
                <span>
                  {answered}/{ITEM_KEYS.length}
                </span>
              </div>
              <div className="total big">
                <span>Průměr</span>
                <span className="dots" />
                <span>{avg === null ? '–' : avg.toFixed(1).replace('.', ',')} / 5</span>
              </div>
              {missing.length > 0 && (
                <p className="alert" role="alert">
                  Chybí ještě {missing.length} {missing.length === 1 ? 'odpověď' : missing.length < 5 ? 'odpovědi' : 'odpovědí'}. Jsou
                  označené červeně.
                </p>
              )}
              {state === 'error' && (
                <p className="alert" role="alert">
                  {error}
                  {errDetail && <small className="err-detail">{errDetail}</small>}
                </p>
              )}
              <button type="submit" className="send" disabled={state === 'sending'}>
                {state === 'sending' ? 'Odesílám…' : 'Odeslat účtenku'}
              </button>
              <p className="small">Rozepsané odpovědi si prohlížeč pamatuje, klidně to dodělej později.</p>
              <p className="barcode" aria-hidden="true">
                ║▌│█║▌│ █║▌│█│║▌║ ▌│█║▌│║
              </p>
            </footer>
          </>
        )}
      </form>
    </main>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
